/**
 * Whitelist of core Claim columns that a product version's claim form fields
 * may be mapped onto (docs/plan/product-version-form-mapping/plan.md).
 *
 * Keys deliberately equal the Claim column names so the future
 * mapping-application engine can assign values directly. Extend consciously —
 * every key added here becomes a user-visible mapping target and a column the
 * lodgement engine must be able to write.
 */

export type MappableClaimFieldValueType = 'text' | 'number' | 'date' | 'boolean';

export type MappableClaimFieldGroup = 'Loss details' | 'Policy' | 'Policy holder' | 'Other';

export interface MappableClaimField {
    /** Claim column name */
    key: string;
    /** UI label (sentence case) */
    label: string;
    group: MappableClaimFieldGroup;
    valueType: MappableClaimFieldValueType;
}

export const MAPPABLE_CLAIM_FIELDS = [
    // Loss details
    { key: 'claimOccurredAt', label: 'Date of loss', group: 'Loss details', valueType: 'date' },
    { key: 'causeOfLoss', label: 'Cause of loss', group: 'Loss details', valueType: 'text' },
    { key: 'addressLine1OfLoss', label: 'Loss address line 1', group: 'Loss details', valueType: 'text' },
    { key: 'addressLine2OfLoss', label: 'Loss address line 2', group: 'Loss details', valueType: 'text' },
    { key: 'suburbOfLoss', label: 'Loss suburb', group: 'Loss details', valueType: 'text' },
    { key: 'stateOfLoss', label: 'Loss state', group: 'Loss details', valueType: 'text' },
    { key: 'postcodeOfLoss', label: 'Loss postcode', group: 'Loss details', valueType: 'text' },
    // Policy
    { key: 'policyNo', label: 'Policy number', group: 'Policy', valueType: 'text' },
    { key: 'policyName', label: 'Policy name', group: 'Policy', valueType: 'text' },
    { key: 'policyTermFrom', label: 'Policy term from', group: 'Policy', valueType: 'date' },
    { key: 'policyTermTo', label: 'Policy term to', group: 'Policy', valueType: 'date' },
    { key: 'policyIssuedOn', label: 'Policy issued on', group: 'Policy', valueType: 'date' },
    { key: 'policyOtherInfo', label: 'Policy other information', group: 'Policy', valueType: 'text' },
    // Policy holder
    { key: 'phCompany', label: 'Policy holder company', group: 'Policy holder', valueType: 'text' },
    { key: 'phABN', label: 'Policy holder ABN', group: 'Policy holder', valueType: 'text' },
    { key: 'phAccountBsb', label: 'Account BSB', group: 'Policy holder', valueType: 'text' },
    { key: 'phAccountName', label: 'Account name', group: 'Policy holder', valueType: 'text' },
    { key: 'phAccountNo', label: 'Account number', group: 'Policy holder', valueType: 'text' },
    { key: 'phIsRegisteredForGst', label: 'Registered for GST', group: 'Policy holder', valueType: 'boolean' },
    { key: 'phITC', label: 'Input tax credit entitlement', group: 'Policy holder', valueType: 'text' },
    // Other
    { key: 'brokerCompany', label: 'Broker company', group: 'Other', valueType: 'text' },
    { key: 'additionalInfo', label: 'Additional information', group: 'Other', valueType: 'text' },
] as const satisfies readonly MappableClaimField[];

export type MappableClaimFieldKey = (typeof MAPPABLE_CLAIM_FIELDS)[number]['key'];

export const MAPPABLE_CLAIM_FIELD_KEYS = MAPPABLE_CLAIM_FIELDS.map((field) => field.key) as [
    MappableClaimFieldKey,
    ...MappableClaimFieldKey[],
];

export const MAPPABLE_CLAIM_FIELDS_BY_KEY: Record<MappableClaimFieldKey, MappableClaimField> = Object.fromEntries(
    MAPPABLE_CLAIM_FIELDS.map((field) => [field.key, field]),
) as Record<MappableClaimFieldKey, MappableClaimField>;

export function isMappableClaimFieldKey(key: string): key is MappableClaimFieldKey {
    return key in MAPPABLE_CLAIM_FIELDS_BY_KEY;
}
